import {RegistrationStep} from 'features/registration';
import {revokePreviewUrl} from './helpers';
import type {RegistrationWizardState} from './useRegistrationWizardState';

type Options = {
	/** Выход без снимка сверки (вход с профиля `?edit=photos`) — например на /profile. */
	onCancelWithoutSnapshot?: () => void;
};

/** Редактирование данных с экрана «Давайте всё сверим» с возможностью отката. */
export const useIdentityReviewEdit = (state: RegistrationWizardState, options: Options = {}) => {
	const {onCancelWithoutSnapshot} = options;
	const {
		documentsUpload,
		foreignDocumentDetails,
		identityEditSnapshot,
		innStep,
		passportDetails,
		personalInfo,
		setDocumentsUpload,
		setForeignDocumentDetails,
		setIdentityEditSnapshot,
		setInnError,
		setInnStep,
		setPassportDetails,
		setPersonalInfo,
		setReturnToIdentityReview,
		setStep,
	} = state;

	/** Начать редактирование данных с экрана «Давайте всё сверим» (с сохранением снимка для отката). */
	const beginIdentityReviewEdit = (target: RegistrationStep) => {
		setIdentityEditSnapshot({
			personalInfo,
			innStep,
			passportDetails,
			foreignDocumentDetails,
			documentsUpload,
		});
		setReturnToIdentityReview(true);
		setStep(target);
	};

	/** Выход из редактирования без сохранения — откат изменений и возврат на сверку. */
	const cancelIdentityReviewEdit = () => {
		const snapshot = identityEditSnapshot;

		if (snapshot) {
			setPersonalInfo(snapshot.personalInfo);
			setInnStep(snapshot.innStep);
			setInnError(null);
			setPassportDetails(snapshot.passportDetails);
			setForeignDocumentDetails(snapshot.foreignDocumentDetails);
			setDocumentsUpload((current) => {
				const slots: typeof current.slots = {};

				for (const [slotId, slot] of Object.entries(current.slots)) {
					const snapshotSlot = snapshot.documentsUpload.slots[slotId];

					// Фото не менялось — оставляем валидное локальное превью.
					if (snapshotSlot && snapshotSlot.photo === slot.photo) {
						slots[slotId] = slot;
						continue;
					}

					// Изменённое фото откатываем: revoke нового превью, на сверке подтянется сохранённое с бэка.
					revokePreviewUrl(slot.previewUrl);
					slots[slotId] = {photo: null, previewUrl: null};
				}

				return {slots, residenceAddress: snapshot.documentsUpload.residenceAddress};
			});
			setIdentityEditSnapshot(null);
		}

		setReturnToIdentityReview(false);

		if (!snapshot && onCancelWithoutSnapshot) {
			onCancelWithoutSnapshot();
			return;
		}

		setStep(RegistrationStep.IdentityReview);
	};

	const clearIdentityEditSnapshot = () => {
		setIdentityEditSnapshot(null);
	};

	/** Смена гражданства со сверки — на «Давайте познакомимся», где выбирается гражданство. */
	const restartFromCitizenship = () => {
		setIdentityEditSnapshot(null);
		setReturnToIdentityReview(false);
		setStep(RegistrationStep.PersonalInfo);
	};

	/** Смена резидентства со сверки — на экран, где выбирается страна проживания. */
	const restartFromResidence = () => {
		setIdentityEditSnapshot(null);
		setReturnToIdentityReview(false);
		setStep(RegistrationStep.PersonalInfo);
	};

	return {
		beginIdentityReviewEdit,
		cancelIdentityReviewEdit,
		clearIdentityEditSnapshot,
		restartFromCitizenship,
		restartFromResidence,
	};
};
